import { Loading } from "@/components"
import { NavigationContainer } from "@react-navigation/native"
import { createNativeStackNavigator } from "@react-navigation/native-stack"
import { VerificationType } from "@taskfortress/core/src/api/types"
import { Platform } from "react-native"
import { useAuth } from "../auth"
import {
  ForgotPasswordScreen,
  LoginScreen,
  ResetPasswordScreen,
  SignupScreen,
  TasksScreen,
  VerifyCodeScreen,
} from "../screens"

// Helper function to format page titles
const formatPageTitle = (title: string): string => {
  if (Platform.OS === "web") {
    return `${title} - TaskFortress`
  }
  return title
}

// Define the auth stack parameter types
export type AuthStackParamList = {
  Login: undefined
  Signup: undefined
  ForgotPassword: undefined
  VerifyCode: {
    email: string
    password?: string
    masterKey?: string
    verificationToken: string
    verificationType: VerificationType
  }
  ResetPassword: {
    email: string
    verificationToken: string
    code: string
  }
}

// Define the app stack parameter types
export type AppStackParamList = {
  Tasks: undefined
}

// Create the stack navigators
const AuthStack = createNativeStackNavigator<AuthStackParamList>()
const AppStack = createNativeStackNavigator<AppStackParamList>()

export const AppNavigator = () => {
  const { isAuthenticated, isLoading } = useAuth()

  // Show loading screen if authentication state is loading
  if (isLoading) {
    return (
      <NavigationContainer>
        <LoadingScreen />
      </NavigationContainer>
    )
  }

  return (
    <NavigationContainer>
      {isAuthenticated ? <AppStackNavigator /> : <AuthStackNavigator />}
    </NavigationContainer>
  )
}

// Authentication stack navigator
const AuthStackNavigator = () => {
  return (
    <AuthStack.Navigator
      initialRouteName="Login"
      screenOptions={{
        headerShown: false,
      }}
    >
      <AuthStack.Screen
        name="Login"
        component={LoginScreen}
        options={{ title: formatPageTitle("Login") }}
      />
      <AuthStack.Screen
        name="Signup"
        component={SignupScreen}
        options={{ title: formatPageTitle("Sign Up") }}
      />
      <AuthStack.Screen
        name="ForgotPassword"
        component={ForgotPasswordScreen}
        options={{ title: formatPageTitle("Forgot Password") }}
      />
      <AuthStack.Screen
        name="VerifyCode"
        component={VerifyCodeScreen}
        options={{ title: formatPageTitle("Verify Code") }}
      />
      <AuthStack.Screen
        name="ResetPassword"
        component={ResetPasswordScreen}
        options={{ title: formatPageTitle("Reset Password") }}
      />
    </AuthStack.Navigator>
  )
}

// Main app stack navigator
const AppStackNavigator = () => {
  return (
    <AppStack.Navigator
      screenOptions={{
        headerShown: false,
      }}
    >
      <AppStack.Screen
        name="Tasks"
        component={TasksScreen}
        options={{ title: formatPageTitle("Tasks") }}
      />
    </AppStack.Navigator>
  )
}

// Simple loading screen component
const LoadingScreen = () => {
  return <Loading />
}
